// pages/ProjectApproval/AwaitingApproval.js
//
// Legacy menu: "View Projects Awaiting Approval" — under Project
// Approval System.
//
// Shows projects that the committee/manager needs to vote on AND that
// the caller has NOT already approved (the backend filters this).
//
// Access (Phase 3 role-based):
//   - hasAccess("View Projects Awaiting Approval"), OR
//   - Manager / President+ / IT Admin
//
// API: GET /api/ProjectApproval/awaiting-approval (built in Phase 3 Pass 2)

import React, { useEffect, useState, useMemo } from "react";
import { ColumnFilterRow, applyColumnFilters } from "../../Components/Common/DashboardKit";
import useTableSort from "../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../Components/Common/DashboardKit/SortableTh";
import exportRowsToCsv from "../../Components/Common/DashboardKit/exportCsv";
import { Container, Card, CardBody, Input, InputGroup, Badge, Alert } from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
    DashboardPageHeader,
    EmptyState,
    StandardTableToolbar, TableSkeleton } from "../../Components/Common/DashboardKit";
import api from "../../api";

const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
};

const StatusBadge = ({ status }) => (
    <Badge color="warning" pill style={{ fontSize: 11 }}>{status || "—"}</Badge>
);

const AwaitingApproval = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");

    // Round PAS-UAT-1 - loader extracted so votes can refresh the list.
    const load = React.useCallback(() => {
        setLoading(true);
        setError(null);
        api.get("ProjectApproval/awaiting-approval")
            .then(({ data }) => {
                setItems(Array.isArray(data?.Items) ? data.Items : []);
            })
            .catch((e) => { setError(e?.response?.data?.message || e?.message || "Failed to load"); })
            .finally(() => { setLoading(false); });
    }, []);

    useEffect(() => { load(); }, [load]);

    // Round PAS-UAT-1 (2026) - in-list voting (module was read-only; the
    // dashboard card promises "Submit / approve"). Approve advances the
    // legacy 3-vote chain (PENDING -> 1/3 -> 2/3 -> APPROVED); Deny sets
    // DENIED. expectedStatus is the optimistic-concurrency token.
    const [votingId, setVotingId] = useState(null);
    const [voteMsg, setVoteMsg] = useState(null);

    const castVote = async (r, action) => {
        const verb = action === "approve" ? "Approve" : "DENY";
        // eslint-disable-next-line no-alert
        if (!window.confirm(`${verb} project #${r.ProjectID} - "${r.ProjectName}"?`)) return;
        setVotingId(r.ProjectID);
        setVoteMsg(null);
        try {
            const res = await api.post(`ProjectApproval/${r.ProjectID}/${action}`, {
                expectedStatus: r.Status,
            });
            setVoteMsg({ color: "success", text: res?.data?.message || "Vote recorded." });
        } catch (err) {
            setVoteMsg({
                color: "danger",
                text: err?.response?.data?.error || err?.message || "Vote failed.",
            });
        } finally {
            setVotingId(null);
            load();
        }
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((r) =>
            String(r.ProjectName ?? "").toLowerCase().includes(q)
            || String(r.CreatedByName ?? "").toLowerCase().includes(q)
            || String(r.Purpose ?? "").toLowerCase().includes(q)
        );
    }, [items, search]);

    // Round 36 — EPO-style per-column filters + click-to-sort + CSV.
    const [columnFilters, setColumnFilters] = useState({});
    const tblColumns = useMemo(() => [
        { id: "ProjectID",   label: "ID",      type: "text", get: (r) => String(r.ProjectID ?? "") },
        { id: "ProjectName", label: "Project", type: "text", get: (r) => r.ProjectName || "" },
        { id: "CreatedByName", label: "Created By", type: "text", get: (r) => r.CreatedByName || r.CreatedBy || "" },
        { id: "TeamLeader",  label: "Team Leader", type: "text", get: (r) => String(r.TeamLeader ?? "") },
        { id: "Type",        label: "Type",    type: "text", get: (r) => r.Type || "" },
        { id: "Status",      label: "Status",  type: "text", get: (r) => r.Status || "" },
        { id: "Requested",   label: "Requested", type: "date", get: (r) => r.RequestDate || "" },
        { id: "NeedBy",      label: "Need By",   type: "date", get: (r) => r.NeedByDate || "" },
        { id: "act", label: "", type: "none" },
    ], []);
    const filteredBase = applyColumnFilters(filtered, tblColumns, columnFilters);
    const { sorted: view, sort, toggleSort } = useTableSort(filteredBase, tblColumns);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    title="Projects Awaiting My Approval"
                    breadcrumb={[{ label: "Projects Awaiting My Approval" }]}
                    subtitle={`Projects needing your committee vote (${items.length})`}
                    breadcrumbItems={[{ label: "Projects" }, { label: "Awaiting Approval" }]}
                />

                {error && <Alert color="danger" className="mt-2">{error}</Alert>}
                {voteMsg && <Alert color={voteMsg.color} className="mt-2" toggle={() => setVoteMsg(null)}>{voteMsg.text}</Alert>}

                <Card className="mt-3">
                    <CardBody>
                        <StandardTableToolbar
                            right={
                                {/* Round 36 — search replaced by the per-column filter row */}
                            }
                        />

                        {loading ? (
                            <div className="table-responsive">
                                <table className="table align-middle mb-0">
                                    <tbody><TableSkeleton rows={6} columns={10} /></tbody>
                                </table>
                            </div>
                        ) : view.length === 0 ? (
                            <EmptyState
                                title="Nothing waiting on you"
                                description="Either there are no projects awaiting approval, or you've already voted on them."
                            />
                        ) : (
                            <div className="table-responsive">
                                {/* Round 36 — CSV of the filtered + sorted list */}
                                <div className="d-flex justify-content-end mb-2">
                                    <button type="button" className="btn btn-soft-primary btn-sm"
                                        onClick={() => exportRowsToCsv("projects-awaitingapproval.csv", tblColumns.filter((c) => c.type !== "none"), view)}>
                                        <i className="ri-download-2-line align-bottom me-1" />Export CSV ({view.length})
                                    </button>
                                </div>
                                <table className="table table-striped table-bordered mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            {tblColumns.map((c) => (
                                                <SortableTh key={c.id} col={c} sort={sort} onToggle={toggleSort} />
                                            ))}
                                        </tr>
                                        <ColumnFilterRow columns={tblColumns} filters={columnFilters} onChange={setColumnFilters} />
                                    </thead>
                                    <tbody>
                                        {view.map((r) => (
                                            <tr key={r.ProjectID}>
                                                <td>{r.ProjectID}</td>
                                                <td style={{ maxWidth: 280 }}>
                                                    <div className="fw-semibold">{r.ProjectName}</div>
                                                    {r.Purpose && (
                                                        <div className="text-muted small text-truncate" title={r.Purpose}>
                                                            {r.Purpose}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>{r.CreatedByName || r.CreatedBy}</td>
                                                <td>{r.TeamLeader}</td>
                                                <td>{r.Type || "—"}</td>
                                                <td><StatusBadge status={r.Status} /></td>
                                                <td>{fmtDate(r.RequestDate)}</td>
                                                <td>{fmtDate(r.NeedByDate)}</td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-link btn-sm p-0"
                                                            onClick={() => navigate(`/apps-projects-overview?id=${r.ProjectID}`)}
                                                        >Review</button>
                                                        {/* Round PAS-UAT-1 - vote in place */}
                                                        <button
                                                            type="button"
                                                            className="btn btn-soft-success btn-sm"
                                                            disabled={votingId === r.ProjectID}
                                                            onClick={() => castVote(r, "approve")}
                                                            title="Record your approval (3 approvals release the project)"
                                                        >
                                                            {votingId === r.ProjectID ? "..." : "Approve"}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-soft-danger btn-sm"
                                                            disabled={votingId === r.ProjectID}
                                                            onClick={() => castVote(r, "deny")}
                                                            title="Deny the project (resets any partial approvals)"
                                                        >
                                                            Deny
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default AwaitingApproval;
